// EXIF 处理：读取方向、强制抹除隐私信息、写回 JPEG(APP1) / PNG(eXIf)
//
// 隐私抹除是强制的、没有开关：位置信息（GPS）、方向、设备序列号、机主信息、
// MakerNote 一律清除；拍摄参数（机型/光圈/快门/ISO/时间）保留。
// 做法：不动任何偏移，只把要删的标签「中性化」，再把对应数据区填充 0。

const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"

const TAG_ORIENTATION = 0x0112;
const TAG_GPS_IFD = 0x8825;
const TAG_EXIF_IFD = 0x8769;
const TAG_INTEROP_IFD = 0xa005;
const TAG_SUB_IFDS = 0x014a;
const TAG_ARTIST = 0x013b;
const TAG_COPYRIGHT = 0x8298;
/** 用来顶替被移除标签的无害标签（TIFF Padding），任何解析器都会跳过 */
const TAG_PADDING = 0xea1c;

/** 一律移除的标签：方向 + 可定位/识别到个人的设备信息 */
const STRIP_TAGS = new Set([
  TAG_ORIENTATION, // 方向（已烘焙进像素，留着会被二次旋转）
  0xa430, // CameraOwnerName 机主姓名
  0xa431, // BodySerialNumber 机身序列号
  0xa435, // LensSerialNumber 镜头序列号
  0xa420, // ImageUniqueID 图像唯一编号
  0xa432, // LensSpecification 镜头规格（随设备）
]);

/** 需要连同数据区一起清零的标签 */
const WIPE_BLOB_TAGS = new Set([
  0x927c, // MakerNote：部分机型会把 GPS 藏在这里
]);

/** 里面可能嵌套子 IFD 的指针标签 */
const SUB_IFD_TAGS = new Set([TAG_EXIF_IFD, TAG_INTEROP_IFD, TAG_SUB_IFDS]);

const TYPE_SIZE: Record<number, number> = {
  1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8,
};

interface Tiff {
  buf: Uint8Array;
  /** TIFF 起始位置（带 "Exif\0\0" 时为 6） */
  base: number;
  little: boolean;
}

function readU16(t: Tiff, off: number): number {
  const o = t.base + off;
  return t.little ? t.buf[o] | (t.buf[o + 1] << 8) : (t.buf[o] << 8) | t.buf[o + 1];
}

function readU32(t: Tiff, off: number): number {
  const o = t.base + off;
  return t.little
    ? (t.buf[o] | (t.buf[o + 1] << 8) | (t.buf[o + 2] << 16) | (t.buf[o + 3] << 24)) >>> 0
    : ((t.buf[o] << 24) | (t.buf[o + 1] << 16) | (t.buf[o + 2] << 8) | t.buf[o + 3]) >>> 0;
}

function writeU16(t: Tiff, off: number, v: number): void {
  const o = t.base + off;
  if (t.little) {
    t.buf[o] = v & 0xff;
    t.buf[o + 1] = (v >> 8) & 0xff;
  } else {
    t.buf[o] = (v >> 8) & 0xff;
    t.buf[o + 1] = v & 0xff;
  }
}

function writeU32(t: Tiff, off: number, v: number): void {
  const o = t.base + off;
  if (t.little) {
    t.buf[o] = v & 0xff;
    t.buf[o + 1] = (v >> 8) & 0xff;
    t.buf[o + 2] = (v >> 16) & 0xff;
    t.buf[o + 3] = (v >> 24) & 0xff;
  } else {
    t.buf[o] = (v >> 24) & 0xff;
    t.buf[o + 1] = (v >> 16) & 0xff;
    t.buf[o + 2] = (v >> 8) & 0xff;
    t.buf[o + 3] = v & 0xff;
  }
}

function tiffLength(t: Tiff): number {
  return t.buf.length - t.base;
}

function isExifBlock(block: Uint8Array): boolean {
  return EXIF_HEADER.every((b, i) => block[i] === b);
}

/** 解析 TIFF 头，失败返回 null */
function openTiff(block: Uint8Array): Tiff | null {
  const base = isExifBlock(block) ? EXIF_HEADER.length : 0;
  if (block.length < base + 8) return null;
  const little = block[base] === 0x49 && block[base + 1] === 0x49;
  const big = block[base] === 0x4d && block[base + 1] === 0x4d;
  if (!little && !big) return null;
  const magic = little
    ? block[base + 2] | (block[base + 3] << 8)
    : (block[base + 2] << 8) | block[base + 3];
  if (magic !== 42) return null;
  return { buf: block, base, little };
}

/** 从 JPEG 字节里取出 EXIF 段（含 "Exif\0\0" 头），没有则返回 null */
export function findJpegExif(bytes: Uint8Array): Uint8Array | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let i = 2;
  while (i + 4 <= bytes.length) {
    if (bytes[i] !== 0xff) break;
    const marker = bytes[i + 1];
    if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      i += 2;
      continue;
    }
    if (marker === 0xda || marker === 0xd9) break; // 进入压缩数据
    const len = (bytes[i + 2] << 8) | bytes[i + 3];
    if (marker === 0xe1) {
      const payload = bytes.subarray(i + 4, i + 2 + len);
      if (isExifBlock(payload)) return payload.slice();
    }
    i += 2 + len;
  }
  return null;
}

/** 读取方向标记（1~8），无则返回 1 */
export function readOrientation(block: Uint8Array | null): number {
  if (!block) return 1;
  const t = openTiff(block);
  if (!t) return 1;
  const len = tiffLength(t);
  let ifd = readU32(t, 4);
  let guard = 0;
  while (ifd > 0 && ifd + 2 <= len && guard++ < 8) {
    const n = readU16(t, ifd);
    if (ifd + 2 + n * 12 + 4 > len) break;
    for (let i = 0; i < n; i++) {
      const e = ifd + 2 + i * 12;
      if (readU16(t, e) === TAG_ORIENTATION) {
        const type = readU16(t, e + 2);
        const count = readU32(t, e + 4);
        if (type === 3 && count >= 1) return readU16(t, e + 8);
        if (type === 4 && count >= 1) return readU32(t, e + 8);
      }
    }
    ifd = readU32(t, ifd + 2 + n * 12);
  }
  return 1;
}

/** 把某个 IFD 里被外部引用的值区清零，并清掉 IFD 自身 */
function zeroIfd(t: Tiff, off: number, depth = 0): void {
  const len = tiffLength(t);
  if (depth > 4 || off <= 0 || off + 2 > len) return;
  const n = readU16(t, off);
  const entriesEnd = off + 2 + n * 12;
  if (entriesEnd + 4 > len) return;

  for (let i = 0; i < n; i++) {
    const e = off + 2 + i * 12;
    const type = readU16(t, e + 2);
    const count = readU32(t, e + 4);
    const size = (TYPE_SIZE[type] ?? 0) * count;
    if (size > 4) {
      const p = readU32(t, e + 8);
      if (p > 0 && p + size <= len) t.buf.fill(0, t.base + p, t.base + p + size);
    }
  }
  t.buf.fill(0, t.base + off, t.base + Math.min(entriesEnd + 4, len));
}

/** 中性化一个标签：改成 Padding + UNDEFINED + 值清零（不移动任何数据） */
function neutralize(t: Tiff, entryOff: number): void {
  writeU16(t, entryOff, TAG_PADDING);
  writeU16(t, entryOff + 2, 7);
  writeU32(t, entryOff + 4, 1);
  t.buf.fill(0, t.base + entryOff + 8, t.base + entryOff + 12);
}

/** 抹掉某个标签引用的整块数据（用于 MakerNote 这类自成一体的数据块） */
function wipeEntryData(t: Tiff, entryOff: number): void {
  const type = readU16(t, entryOff + 2);
  const count = readU32(t, entryOff + 4);
  const size = (TYPE_SIZE[type] ?? 0) * count;
  const len = tiffLength(t);
  if (size > 4) {
    const off = readU32(t, entryOff + 8);
    if (off > 0 && off + size <= len) t.buf.fill(0, t.base + off, t.base + off + size);
  }
}

/** 读取一个「指向 IFD」的条目里的所有指针（SubIFDs 可能有多个） */
function readPointers(t: Tiff, entryOff: number): number[] {
  const type = readU16(t, entryOff + 2);
  const count = readU32(t, entryOff + 4);
  if (type !== 4 && type !== 13) return [];
  const len = tiffLength(t);
  if (count <= 1) return [readU32(t, entryOff + 8)];
  const start = readU32(t, entryOff + 8);
  const out: number[] = [];
  for (let i = 0; i < count && i < 16; i++) {
    if (start + i * 4 + 4 > len) break;
    out.push(readU32(t, start + i * 4));
  }
  return out;
}

/**
 * 强制抹除隐私信息，返回新的 EXIF 段（没有开关，调用即删除）：
 * - GPS 子 IFD 及其所有数值区被填充 0，指针标签被顶替 → 无法再读出位置
 * - 方向、机主姓名、机身/镜头序列号、图像唯一编号、镜头规格被顶替
 * - MakerNote 数据区被填充 0（部分机型会把 GPS 藏在里面）
 * 会递归进入 ExifIFD / InteropIFD / SubIFDs / 缩略图 IFD。
 */
export function stripPrivateTags(block: Uint8Array): Uint8Array {
  const out = block.slice();
  const t = openTiff(out);
  if (!t) return out;
  const len = tiffLength(t);
  const visited = new Set<number>();

  const walk = (ifdOff: number, depth: number): void => {
    if (depth > 6 || ifdOff <= 0 || ifdOff + 2 > len || visited.has(ifdOff)) return;
    visited.add(ifdOff);
    const n = readU16(t, ifdOff);
    if (ifdOff + 2 + n * 12 + 4 > len) return;

    for (let i = 0; i < n; i++) {
      const e = ifdOff + 2 + i * 12;
      const tag = readU16(t, e);

      if (tag === TAG_GPS_IFD) {
        zeroIfd(t, readU32(t, e + 8));
        neutralize(t, e);
        continue;
      }
      if (WIPE_BLOB_TAGS.has(tag)) {
        wipeEntryData(t, e);
        neutralize(t, e);
        continue;
      }
      if (STRIP_TAGS.has(tag)) {
        // 连数据区一起清零：否则序列号/机主姓名等字节虽然失去了标签指向，仍留在文件里
        wipeEntryData(t, e);
        neutralize(t, e);
        continue;
      }
      if (SUB_IFD_TAGS.has(tag)) {
        for (const p of readPointers(t, e)) walk(p, depth + 1);
      }
    }

    walk(readU32(t, ifdOff + 2 + n * 12), depth + 1); // IFD1 缩略图等
  };

  walk(readU32(t, 4), 0);
  return out;
}

/**
 * 把版权署名写进 IFD0 的 Copyright(0x8298) 与 Artist(0x013B)。
 * 做法：字符串追加在 TIFF 末尾（不移动任何已有偏移），条目优先复用
 * stripPrivateTags 抹隐私时留下的空槽位；已有同名字段则原地改写（顺带能更新年份）。
 * 没有任何可用槽位时返回原字节（此时只有 JPEG 注释段兜底）。
 */
export function injectCopyrightFields(block: Uint8Array, text: string): Uint8Array {
  const trimmed = text.trim();
  const t = openTiff(block);
  if (!t || !trimmed) return block.slice();

  const ifd0 = readU32(t, 4);
  const len = tiffLength(t);
  if (ifd0 <= 0 || ifd0 + 2 > len) return block.slice();
  const count = readU16(t, ifd0);
  if (ifd0 + 2 + count * 12 + 4 > len) return block.slice();

  const findEntry = (tag: number): number => {
    for (let i = 0; i < count; i++) {
      const e = ifd0 + 2 + i * 12;
      if (readU16(t, e) === tag) return e;
    }
    return 0;
  };
  const slots: number[] = [];
  for (let i = 0; i < count; i++) {
    const e = ifd0 + 2 + i * 12;
    if (readU16(t, e) === TAG_PADDING) slots.push(e);
  }

  const artistEntry = findEntry(TAG_ARTIST);
  const copyrightEntry = findEntry(TAG_COPYRIGHT);
  if (!artistEntry && !copyrightEntry && slots.length === 0) return block.slice();

  const bytes = new TextEncoder().encode(trimmed);
  const fieldLen = bytes.length + 1; // 结尾补一个 NUL
  const need = fieldLen * 2;
  const out = new Uint8Array(block.length + need);
  out.set(block, 0);
  const t2: Tiff = { buf: out, base: t.base, little: t.little };

  // 追加区（相对 TIFF 起点），放在所有已有数据之后，因此不会影响任何已有偏移
  const copyrightOff = len;
  const artistOff = len + fieldLen;
  out.set(bytes, t.base + copyrightOff);
  out[t.base + copyrightOff + bytes.length] = 0;
  out.set(bytes, t.base + artistOff);
  out[t.base + artistOff + bytes.length] = 0;

  const writeTag = (entryOff: number, tag: number, valueOff: number): void => {
    writeU16(t2, entryOff, tag);
    writeU16(t2, entryOff + 2, 2); // ASCII
    writeU32(t2, entryOff + 4, fieldLen);
    writeU32(t2, entryOff + 8, valueOff);
  };

  // 已有字段 → 原地改写（指向新字符串）；否则占用空槽位
  let slot = 0;
  if (copyrightEntry) writeTag(copyrightEntry, TAG_COPYRIGHT, copyrightOff);
  else if (slot < slots.length) writeTag(slots[slot++], TAG_COPYRIGHT, copyrightOff);

  if (artistEntry) writeTag(artistEntry, TAG_ARTIST, artistOff);
  else if (slot < slots.length) writeTag(slots[slot++], TAG_ARTIST, artistOff);

  return out;
}

/** 组装 APP1 段（FF E1 + 长度 + "Exif\0\0" + TIFF） */
function app1Segment(exif: Uint8Array): Uint8Array {
  const seg = new Uint8Array(exif.length + 4);
  seg[0] = 0xff;
  seg[1] = 0xe1;
  const len = exif.length + 2;
  seg[2] = (len >> 8) & 0xff;
  seg[3] = len & 0xff;
  seg.set(exif, 4);
  return seg;
}

/** 把 EXIF 段插入 JPEG：保留 APP0(JFIF)，丢弃自带的旧 EXIF，再放新的 */
export function injectJpegExif(jpeg: Uint8Array, exif: Uint8Array): Uint8Array {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return jpeg;
  const head: Uint8Array[] = [jpeg.subarray(0, 2)];
  let i = 2;

  while (i + 4 <= jpeg.length && jpeg[i] === 0xff && jpeg[i + 1] === 0xe0) {
    const len = (jpeg[i + 2] << 8) | jpeg[i + 3];
    head.push(jpeg.subarray(i, i + 2 + len));
    i += 2 + len;
  }
  while (i + 4 <= jpeg.length && jpeg[i] === 0xff && jpeg[i + 1] === 0xe1) {
    const len = (jpeg[i + 2] << 8) | jpeg[i + 3];
    if (!isExifBlock(jpeg.subarray(i + 4, i + 2 + len))) break;
    i += 2 + len; // 丢掉旧 EXIF，避免重复
  }

  head.push(app1Segment(exif), jpeg.subarray(i));
  const total = head.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of head) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

/**
 * 把版权署名写成 JPEG 注释段（COM，FFFE）。
 * 这是"兜底"通道：即使 EXIF 里塞不进（没有空槽位），文件本身仍带着版权声明。
 */
export function injectJpegComment(jpeg: Uint8Array, text: string): Uint8Array {
  const body = new TextEncoder().encode(text.trim());
  if (body.length === 0 || body.length > 65500) return jpeg;
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) return jpeg;

  const seg = new Uint8Array(4 + body.length);
  seg[0] = 0xff;
  seg[1] = 0xfe;
  const len = body.length + 2;
  seg[2] = (len >> 8) & 0xff;
  seg[3] = len & 0xff;
  seg.set(body, 4);

  const head: Uint8Array[] = [jpeg.subarray(0, 2)];
  let i = 2;
  while (i + 4 <= jpeg.length && jpeg[i] === 0xff) {
    const marker = jpeg[i + 1];
    if (marker !== 0xe0 && marker !== 0xe1 && marker !== 0xfe) break;
    const len2 = (jpeg[i + 2] << 8) | jpeg[i + 3];
    if (marker !== 0xfe) head.push(jpeg.subarray(i, i + 2 + len2)); // 保留 APP0/APP1
    i += 2 + len2; // 丢掉旧注释，避免重复
  }
  head.push(seg, jpeg.subarray(i));

  const total = head.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let off = 0;
  for (const p of head) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** 组装一个 PNG 数据块：长度 + 类型 + 数据 + CRC */
function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const chunk = new Uint8Array(12 + data.length);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) chunk[4 + i] = type.charCodeAt(i);
  chunk.set(data, 8);
  view.setUint32(8 + data.length, crc32(chunk.subarray(4, 8 + data.length)));
  return chunk;
}

/** 把若干数据块插到第一个 IDAT 之前（IEND/IDAT 之后不能再插） */
function insertPngChunks(png: Uint8Array, chunks: Uint8Array[]): Uint8Array {
  const sig = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (png.length < 8 || !sig.every((b, i) => png[i] === b)) return png;
  if (chunks.length === 0) return png;

  let off = 8;
  while (off + 8 <= png.length) {
    const len = new DataView(png.buffer, png.byteOffset + off, 4).getUint32(0);
    const type = String.fromCharCode(png[off + 4], png[off + 5], png[off + 6], png[off + 7]);
    if (type === "IDAT" || type === "IEND") break;
    off += 12 + len;
  }
  if (off + 8 > png.length) return png;

  const extra = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(png.length + extra);
  out.set(png.subarray(0, off), 0);
  let p = off;
  for (const c of chunks) {
    out.set(c, p);
    p += c.length;
  }
  out.set(png.subarray(off), p);
  return out;
}

/** 把 EXIF 写入 PNG：插入 eXIf 数据块（内容为裸 TIFF） */
export function injectPngExif(png: Uint8Array, exif: Uint8Array): Uint8Array {
  const tiff = exif.subarray(isExifBlock(exif) ? EXIF_HEADER.length : 0);
  return insertPngChunks(png, [pngChunk("eXIf", tiff)]);
}

/** 写入 PNG 文本块（iTXt，UTF-8，可放中文），用于版权/作者声明 */
export function injectPngText(png: Uint8Array, fields: { keyword: string; value: string }[]): Uint8Array {
  const encoder = new TextEncoder();
  const chunks: Uint8Array[] = [];
  for (const field of fields) {
    const value = field.value.trim();
    if (!value) continue;
    const kw = encoder.encode(field.keyword);
    const val = encoder.encode(value);
    // iTXt 结构：keyword \0 压缩标志 \0? 语言标签 \0 翻译关键字 \0 文本
    const data = new Uint8Array(kw.length + 1 + 2 + 1 + 1 + val.length);
    data.set(kw, 0);
    const p = kw.length;
    data[p] = 0; // keyword 结束
    data[p + 1] = 0; // 未压缩
    data[p + 2] = 0; // 压缩方法
    data[p + 3] = 0; // 空语言标签
    data[p + 4] = 0; // 空翻译关键字
    data.set(val, p + 5);
    chunks.push(pngChunk("iTXt", data));
  }
  return insertPngChunks(png, chunks);
}
